/**
 * Where one selected tenant's copy of the announcement lands.
 *
 * Each row loads its OWN versions and pages: platform versions are per tenant and do not line up
 * across them, and a page slug only exists in its tenant's dashboard. Owning the fetch here keeps
 * the parent free of a hook-per-tenant loop.
 *
 * The flag-key state is shown here rather than only in step 2, because it is the cheapest place to
 * notice that a BU cannot gate items before writing content that depends on it.
 *
 * Use cases are **this tenant's own** and can be picked in any number. The BFF stores one use case
 * per record, so each extra one adds a record — the chips say so rather than hiding it.
 *
 * @module Pages/BUs/WhatsNew
 */

import { css } from '@emotion/css';
import type { JSX } from 'react';
import { useTenantVersions } from '../../../Hooks/useTenantVersions';
import type { TenantTarget } from '../../../Utils/buildPayload';
import type { TenantUseCase } from '@/Pages/BUs/BUManager/Components/BUPlatformList/BUPlatformList.types';
import { useTenantPages } from '@/Pages/BUs/Pages/Hooks/useTenantPages';
import { IconButton } from '@/Shared/components/IconButton/IconButton';
import { Tag } from '@/Shared/components/Tag/Tag';

const rowStyle = css`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(160px, 100%), 1fr));
  gap: var(--space-sm) var(--space-m);
  align-items: end;
  padding: var(--space-m);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-m);
`;

const identityStyle = css`
  display: flex;
  align-items: flex-start;
  gap: var(--space-s);
  min-width: 0;
`;

const identityTextStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
`;

const labelStyle = css`
  font-size: 10px;
  letter-spacing: var(--letter-spacing-caps);
  text-transform: uppercase;
  color: var(--color-text-muted);
`;

const slugStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const flagStateStyle = css`
  font-size: var(--font-size-2xs);
  font-weight: var(--font-weight-semibold);
`;

const fieldStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

/** Spans the row: chips wrap, unlike the selects beside them. */
const useCaseFieldStyle = css`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  grid-column: 1 / -1;
`;

const chipsStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-s);
`;

const useCaseHintStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
`;

const emptyUseCaseStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

const selectStyle = css`
  height: 36px;
  padding: 0 var(--space-s);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-control);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`;

export interface TenantTargetRowProps {
  tenantSlug: string;
  target: TenantTarget;
  /** This tenant's own use cases; only the active ones are offered. */
  useCases: TenantUseCase[];
  /** Whether the BU has a Flagsmith environment key. */
  hasFlagKey: boolean;
  /** False for the last remaining tenant — an announcement needs at least one. */
  canRemove: boolean;
  onChange: (tenantSlug: string, target: TenantTarget) => void;
  onRemove: (tenantSlug: string) => void;
}

export const TenantTargetRow = ({
  tenantSlug,
  target,
  useCases,
  hasFlagKey,
  canRemove,
  onChange,
  onRemove,
}: TenantTargetRowProps): JSX.Element => {
  const { versions, isLoadingVersions } = useTenantVersions({ tenantSlug });
  const { pages, isLoading: isLoadingPages } = useTenantPages({ tenantSlug });

  const activePages = pages.filter(page => page.isActive);
  const activeUseCases = useCases.filter(useCase => useCase.isActive);

  return (
    <div className={rowStyle}>
      <div className={identityStyle}>
        <div className={identityTextStyle}>
          <span className={labelStyle}>Tenant</span>
          <span className={slugStyle}>{tenantSlug}</span>
          <span
            className={flagStateStyle}
            style={{ color: hasFlagKey ? 'var(--color-success)' : 'var(--color-danger)' }}
          >
            {hasFlagKey ? 'flags ok' : 'sem key de flags'}
          </span>
        </div>
        {canRemove ? (
          <IconButton
            icon='Arrows.X'
            size='sm'
            aria-label={`Remover ${tenantSlug}`}
            onClick={() => {
              onRemove(tenantSlug);
            }}
          />
        ) : null}
      </div>

      <div className={fieldStyle}>
        <span className={labelStyle}>Versão da plataforma</span>
        <select
          className={selectStyle}
          value={target.platformVersionId}
          aria-label={`Versão de ${tenantSlug}`}
          onChange={event => {
            onChange(tenantSlug, { ...target, platformVersionId: event.target.value });
          }}
        >
          <option value=''>{isLoadingVersions ? 'Carregando…' : 'Selecione a versão'}</option>
          {versions.map(version => (
            <option key={version.id} value={version.id}>
              {version.version}
              {version.isCurrent ? ' — atual' : ''}
            </option>
          ))}
        </select>
      </div>

      <div className={fieldStyle}>
        <span className={labelStyle}>Página</span>
        <select
          className={selectStyle}
          value={target.pageId ?? ''}
          aria-label={`Página de ${tenantSlug}`}
          onChange={event => {
            onChange(tenantSlug, { ...target, pageId: event.target.value || undefined });
          }}
        >
          <option value=''>
            {isLoadingPages ? 'Carregando…' : 'Página principal da BU (padrão)'}
          </option>
          {activePages.map(page => (
            <option key={page.id} value={page.id}>
              {page.name} ({page.slug})
            </option>
          ))}
        </select>
      </div>

      <div className={useCaseFieldStyle}>
        <span className={labelStyle}>Use cases</span>
        {activeUseCases.length === 0 ? (
          <span className={emptyUseCaseStyle}>
            Esta BU não tem use case cadastrado — o anúncio vai para todos os usuários dela.
          </span>
        ) : (
          <>
            <div className={chipsStyle}>
              {activeUseCases.map(useCase => (
                <Tag
                  key={useCase.id}
                  tone='primary'
                  selected={target.useCaseSlugs.includes(useCase.slug)}
                  onClick={() => {
                    const selected = target.useCaseSlugs.includes(useCase.slug)
                      ? target.useCaseSlugs.filter(slug => slug !== useCase.slug)
                      : [...target.useCaseSlugs, useCase.slug];
                    onChange(tenantSlug, { ...target, useCaseSlugs: selected });
                  }}
                >
                  {useCase.name}
                </Tag>
              ))}
            </div>
            <span className={useCaseHintStyle}>
              {target.useCaseSlugs.length === 0
                ? 'Nenhum escolhido — todos os usuários do tenant.'
                : `${target.useCaseSlugs.length} escolhidos — ${target.useCaseSlugs.length} registros para este tenant.`}
            </span>
          </>
        )}
      </div>
    </div>
  );
};
